<template>
  <div class="invoice-index-page invoices main-content">
    <div class="page-header">
      <h3 class="page-title">{{ $t("payments.title") }}</h3>
      <ol class="breadcrumb">
        <li class="breadcrumb-item">
          <router-link slot="item-title" to="dashboard">
            {{ $t("general.home") }}
          </router-link>
        </li>
        <li class="breadcrumb-item">
          <router-link slot="item-title" to="#">
            {{ $tc("payments.payment", 2) }}
          </router-link>
        </li>
      </ol>
      <div class="page-actions row">
        <div class="col-xs-2 mr-4">
          <base-button
            v-show="totalPayments || filtersApplied"
            :outline="true"
            :icon="filterIcon"
            color="theme"
            right-icon
            size="large"
            @click="toggleFilter"
          >
            {{ $t("general.filter") }}
          </base-button>
        </div>
        <!---payment create case: only supplier or anchor-->
        <a
          v-if="
            userType == 'supplier' ||
              userType == 'anchor' ||
              userType == 'funder'
          "
          slot="item-title"
          class="col-xs-2"
          href="#"
          @click="addPayment()"
        >
          <base-button color="theme" icon="plus" size="large">
            {{ $t("payments.add_payment") }}
          </base-button>
        </a>
      </div>
    </div>

    <transition name="fade" mode="out-in">
      <div v-show="showFilters" class="filter-section">
        <div class="row">
          <div class="col-md-4">
            <label class="form-label">{{ $t("general.user") }}</label>
            <base-select
              v-model="filters.user"
              :options="users"
              :group-select="false"
              :searchable="true"
              :show-labels="false"
              :placeholder="$t('general.select_a_user')"
              group-values="options"
              group-label="label"
              track-by="id"
              label="name"
            />
          </div>

          <div class="col-sm-4">
            <label for="">{{ $t("invoices.invoice_number") }}</label>
            <base-input
              v-model="filters.payment_number"
              :placeholder="$t(payments.payment_number)"
              name="payment_number"
            />
          </div>
          <div class="col-sm-4">
            <label class="form-label">{{ $t("payments.payment_mode") }}</label>
            <base-select
              v-model="filters.payment_mode"
              :options="paymentModes"
              :searchable="true"
              :show-labels="false"
              :placeholder="$t('payments.payment_mode')"
              label="name"
            />
          </div>
          <div class="col-sm-4">
            <label class="form-label">{{ $t("general.from") }}</label>
            <base-date-picker
              v-model="filters.from_date"
              :calendar-button="true"
              calendar-button-icon="calendar"
            />
          </div>
          <div class="col-sm-4">
            <label class="form-label">{{ $t("general.to") }}</label>
            <base-date-picker
              v-model="filters.to_date"
              :calendar-button="true"
              calendar-button-icon="calendar"
            />
          </div>
        </div>
        <label class="clear-filter" @click="clearFilter">{{
          $t("general.clear_all")
        }}</label>
      </div>
    </transition>

    <div
      v-cloak
      v-show="showEmptyScreen"
      class="col-xs-1 no-data-info"
      align="center"
    >
      <capsule-icon class="mt-5 mb-4" />
      <div class="row" align="center">
        <label class="col title">{{ $t("payments.no_payments") }}</label>
      </div>
      <div class="row">
        <label class="description col mt-1" align="center">{{
          $t("payments.list_of_payments")
        }}</label>
      </div>
      <!---payment create case: only supplier or anchor-->
      <div
        v-if="
          userType == 'supplier' || userType == 'anchor' || userType == 'funder'
        "
        class="btn-container"
      >
        <base-button
          :outline="true"
          color="theme"
          class="mt-3"
          size="large"
          @click="addPayment()"
        >
          {{ $t("payments.add_new_payment") }}
        </base-button>
      </div>
    </div>

    <div v-show="!showEmptyScreen" class="table-container">
      <div class="table-actions mt-5">
        <p class="table-stats">
          {{ $t("general.showing") }}: <b>{{ payments.length }}</b>
          {{ $t("general.of") }} <b>{{ totalPayments }}</b>
        </p>
        <ul class="tabs">
          <li class="tab" @click="getAll()">
            <a
              :class="[
                'tab-link',
                { 'a-active': filters.sent === '' && filters.receive === '' }
              ]"
              href="#"
              >{{ $t("general.all") }}</a
            >
          </li>
          <li
            v-if="userType == 'supplier' || userType == 'funder'"
            class="tab"
            @click="getPaymentSent('true')"
          >
            <a
              :class="['tab-link', { 'a-active': filters.sent === 'true' }]"
              href="#"
              >{{ $t("general.payment_sent") }}</a
            >
          </li>
          <li
            v-if="userType == 'supplier' || userType == 'funder'"
            class="tab"
            @click="getPaymentReceive('true')"
          >
            <a
              :class="['tab-link', { 'a-active': filters.receive === 'true' }]"
              href="#"
              >{{ $t("general.payment_received") }}</a
            >
          </li>
        </ul>
        <transition name="fade">
          <v-dropdown v-if="selectedPayments.length" :show-arrow="false">
            <span
              slot="activator"
              href="#"
              class="table-actions-button dropdown-toggle"
            >
              {{ $t("general.actions") }}
            </span>
            <v-dropdown-item>
              <div class="dropdown-item" @click="removeMultiplePayments">
                <font-awesome-icon
                  :icon="['fas', 'trash']"
                  class="dropdown-item-icon"
                />
                {{ $t("general.delete") }}
              </div>
            </v-dropdown-item>
          </v-dropdown>
        </transition>
      </div>

      <div class="custom-control custom-checkbox">
        <input
          id="select-all"
          v-model="selectAllFieldStatus"
          type="checkbox"
          class="custom-control-input"
          @change="selectAllPayments"
        />
        <label
          v-show="!isRequestOngoing"
          for="select-all"
          class="custom-control-label selectall"
        >
          <span class="select-all-label">{{ $t("general.select_all") }} </span>
        </label>
      </div>

      <table-component
        ref="table"
        :data="fetchData"
        :show-filter="false"
        table-class="table"
      >
        <table-column
          :sortable="false"
          :filterable="false"
          cell-class="no-click"
        >
          <template slot-scope="row">
            <div class="custom-control custom-checkbox">
              <input
                :id="row.id"
                v-model="selectField"
                :value="row.id"
                type="checkbox"
                class="custom-control-input"
              />
              <label :for="row.id" class="custom-control-label" />
            </div>
          </template>
        </table-column>
        <table-column
          :label="$t('payments.date')"
          sort-as="payment_date"
          show="formattedPaymentDate"
        />
        <table-column
          :label="$t('payments.invoice_num')"
          sort-as="invoice_id"
          show="payable.reference_number"
        />
        <table-column
          :label="$t('payments.payment_number')"
          show="rtgs_number"
        />
        <table-column :label="$t('general.sent')">
          <template slot-scope="row">
            <p v-if="row.user">
              <span
                v-if="
                  row.user.company &&
                    row.user.company.name
                "
              >
                {{ row.user.company.name }}
              </span>
              <!-- {{ row.sent_stakeholder.details.user.name }} -->
            </p>
          </template>
        </table-column>
        <table-column :label="$t('general.received')">
          <template slot-scope="row">
            <p v-if="row.receiver">
              <span
                v-if="
                  row.receiver.company &&
                    row.receiver.company.name
                "
              >
                {{ row.receiver.company.name }}
              </span>
              <!-- {{ row.receiver_stakeholder.details.user.name }} -->
            </p>
          </template>
        </table-column>
        <table-column
          :label="$t('payments.payment_mode')"
          show="payment_mode"
        />
        <table-column :label="$t('payments.amount')">
          <template slot-scope="row">
            <span>{{ $t("payments.amount") }}</span>
            <div
              v-if="row.user.currency"
              v-html="
                $utils.formatMoney(
                  row.amount,
                  row.user.currency
                )
              "
            />
            <div
              v-else
              v-html="$utils.formatMoney(row.amount, row.customer.currency)"
            />
          </template>
        </table-column>

        <table-column
          :sortable="false"
          :filterable="false"
          cell-class="action-dropdown no-click"
        >
          <template slot-scope="row">
            <span>{{ $t("payments.action") }}</span>
            <v-dropdown>
              <a slot="activator" href="#">
                <dot-icon />
              </a>

              <v-dropdown-item>
                <router-link
                  :to="{ path: `payments/${row.id}/view` }"
                  class="dropdown-item"
                >
                  <font-awesome-icon icon="eye" class="dropdown-item-icon" />
                  {{ $t("general.view") }}
                </router-link>
              </v-dropdown-item>
            </v-dropdown>
          </template>
        </table-column>
      </table-component>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { SweetModal, SweetModalTab } from "sweet-modal-vue";
import CapsuleIcon from "../../components/icon/CapsuleIcon";
import BaseButton from "../../../js/components/base/BaseButton";
import moment from "moment";
export default {
  components: {
    "capsule-icon": CapsuleIcon,
    SweetModal: SweetModal,
    SweetModalTab: SweetModalTab,
    BaseButton
  },
  data() {
    return {
      userType: null,
      showFilters: false,
      sortedBy: "created_at",
      filtersApplied: false,
      isRequestOngoing: true,
      filters: {
        user: "",
        customer: "",
        payment_mode: "",
        payment_number: "",
        sent: "",
        receive: "",
        from_date: "",
        to_date: ""
      },
      newParentCustomersArray: [],
      newFunderArray: [],
      newClientArray: [],
      newSupplierArray: [],
      newAccountArray: []
    };
  },
  computed: {
    showEmptyScreen() {
      return (
        !this.totalPayments && !this.isRequestOngoing && !this.filtersApplied
      );
    },
    filterIcon() {
      return this.showFilters ? "times" : "filter";
    },
    ...mapGetters("customer", [
      "customers",
      "parentCustomers",
      "simpleCustomers"
    ]),
    ...mapGetters("funder", ["funders"]),
    ...mapGetters("payment", [
      "selectedPayments",
      "totalPayments",
      "payments",
      "selectAllField",
      "paymentModes"
    ]),
    ...mapGetters("account", ["accounts"]),
    ...mapGetters("supplier", ["suppliers"]),
    users: {
      get: function() {
        if (this.userType == "anchor") {
          return [
            {
              label: "Suppliers",
              isDisable: true,
              options: this.newSupplierArray
            }
          ];
        }
        if (this.userType == "super_admin") {
          return [
            {
              label: "Accounts",
              isDisable: true,
              options: this.newAccountArray
            }
          ];
        }
        if (this.userType == "funder") {
          return [
            {
              label: "Customers",
              isDisable: true,
              options: this.newClientArray
            },
            {
              label: "Super Admin",
              options: [
                {
                  id: 1,
                  name: "Super Admin"
                }
              ]
            }
          ];
        }
        if (this.userType == "supplier") {
          return [
            {
              label: "Buyer Customers",
              isDisable: true,
              options: this.newParentCustomersArray
            },
            {
              label: "Funders",
              isDisable: true,
              options: this.newFunderArray
            },
            {
              label: "Super Admin",
              options: [
                {
                  id: 1,
                  name: "Super Admin"
                }
              ]
            }
          ];
        }
      },
      set: function(val) {
        this.filters.user = this.val;
      }
    },

    selectField: {
      get: function() {
        return this.selectedPayments;
      },
      set: function(val) {
        this.selectPayment(val);
      }
    },
    selectAllFieldStatus: {
      get: function() {
        return this.selectAllField;
      },
      set: function(val) {
        this.setSelectAllState(val);
      }
    }
  },
  watch: {
    filters: {
      handler: "setFilters",
      deep: true
    }
  },
  created() {
    let data = {
      pagination: "no"
    };
    if (localStorage.getItem("role") == "super_admin") {
      this.userType = "super_admin";
      this.getAccounts();
    } else if (localStorage.getItem("account_type") == "anchor") {
      this.userType = "anchor";
      this.getSuppliers();
    } else if (localStorage.getItem("account_type") == "funder") {
      this.userType = "funder";
      this.getClients();
    } else if (localStorage.getItem("account_type") == "supplier") {
      this.userType = "supplier";
      this.fetchSimpleCustomers();
      this.getFunders();
    }
  },
  mounted() {},
  destroyed() {
    if (this.selectAllField) {
      this.selectAllPayments();
    }
  },
  methods: {
    ...mapActions("payment", [
      "fetchPayments",
      "selectAllPayments",
      "selectPayment",
      "deletePayment",
      "deleteMultiplePayments",
      "setSelectAllState"
    ]),
    ...mapActions("customer", [
      "fetchCustomers",
      "fetchSimpleCustomers",
      "fetchParentCustomers"
    ]),
    ...mapActions("funder", ["fetchFunders"]),
    ...mapActions("account", ["fetchAccounts"]),
    ...mapActions("supplier", ["fetchSuppliers"]),
    async getAccounts() {
      let res = await this.fetchAccounts({ pagination: "no", role: "admin" });
      let accountArray = res.data.accounts.data;
      this.newAccountArray = accountArray.map(function(row) {
        return { id: row.company_id, name: row.company_name };
      });
    },
    async getSuppliers() {
      let res = await this.fetchSuppliers({ pagination: "no", role: "admin" });
      let supplierArray = res.data.suppliers.data;
      this.newSupplierArray = supplierArray.map(function(row) {
        return { id: row.company_id, name: row.company_name };
      });
    },
    async getClients() {
      let res = await this.fetchCustomers({ pagination: "no" });
      let customerArray = res.data.customers.data;
      this.newClientArray = customerArray.map(function(row) {
        return { id: row.company_id, name: row.company_name };
      });
    },
    async getFunders() {
      let res = await this.fetchFunders({ pagination: "no" });
      let funderArray = res.data.funders.data;
      this.newFunderArray = funderArray.map(function(row) {
        return { id: row.company_id, name: row.company_name };
      });
    },
    async getBuyerCustomers() {
      let res = await this.fetchParentCustomers();
      let parentCustomersArray = res.data.customers;
      this.newParentCustomersArray = parentCustomersArray.map(function(row) {
        return {
          id: row.parent_account.company.id,
          name: row.name,
          customer_id: row.id
        };
      });
    },
    async fetchData({ page, per_page, filter, sort }) {
      let data = {
        company_id:
          this.filters.user === "" ? this.filters.user : this.filters.user.id,
        payment_sent: this.filters.sent !== null ? this.filters.sent : "",
        payment_receiver:
          this.filters.receive !== null ? this.filters.receive : "",
        payment_number: this.filters.payment_number,
        payment_method_id: this.filters.payment_mode
          ? this.filters.payment_mode.id
          : "",
        from_date:
          this.filters.from_date === ""
            ? this.filters.from_date
            : moment(this.filters.from_date).format("DD/MM/YYYY"),
        to_date:
          this.filters.to_date === ""
            ? this.filters.to_date
            : moment(this.filters.to_date).format("DD/MM/YYYY"),
        orderByField: sort.fieldName || "created_at",
        orderBy: sort.order || "desc",
        per_page, // Add rowsPerPage in the API request
        page
      };
      let anchorData = {
        company_id:
          this.filters.user === "" ? this.filters.user : this.filters.user.id,
        payment_sent: this.filters.sent !== null ? this.filters.sent : "",
        payment_receiver:
          this.filters.receive !== null ? this.filters.receive : "",
        payment_number: this.filters.payment_number,
        payment_method_id: this.filters.payment_mode
          ? this.filters.payment_mode.id
          : "",
        from_date:
          this.filters.from_date === ""
            ? this.filters.from_date
            : moment(this.filters.from_date).format("DD/MM/YYYY"),
        to_date:
          this.filters.to_date === ""
            ? this.filters.to_date
            : moment(this.filters.to_date).format("DD/MM/YYYY"),
        orderByField: sort.fieldName || "created_at",
        orderBy: sort.order || "desc",
        per_page, // Add rowsPerPage in the API request
        page
      };
      let funderData = {
        company_id:
          this.filters.user === "" ? this.filters.user : this.filters.user.id,
        payment_sent: this.filters.sent !== null ? this.filters.sent : "",
        payment_receiver:
          this.filters.receive !== null ? this.filters.receive : "",
        payment_number: this.filters.payment_number,
        payment_method_id: this.filters.payment_mode
          ? this.filters.payment_mode.id
          : "",
        from_date:
          this.filters.from_date === ""
            ? this.filters.from_date
            : moment(this.filters.from_date).format("DD/MM/YYYY"),
        to_date:
          this.filters.to_date === ""
            ? this.filters.to_date
            : moment(this.filters.to_date).format("DD/MM/YYYY"),
        orderByField: sort.fieldName || "created_at",
        orderBy: sort.order || "desc",
        per_page, // Add rowsPerPage in the API request
        page
      };

      let superadminData = {
        user:
          this.filters.user === "" ? this.filters.user : this.filters.user.id,
        payment_sent: this.filters.sent !== null ? this.filters.sent : "",
        payment_receiver:
          this.filters.receive !== null ? this.filters.receive : "",
        payment_number: this.filters.payment_number,
        payment_method_id: this.filters.payment_mode
          ? this.filters.payment_mode.id
          : "",
        from_date:
          this.filters.from_date === ""
            ? this.filters.from_date
            : moment(this.filters.from_date).format("DD/MM/YYYY"),
        to_date:
          this.filters.to_date === ""
            ? this.filters.to_date
            : moment(this.filters.to_date).format("DD/MM/YYYY"),
        orderByField: sort.fieldName || "created_at",
        orderBy: sort.order || "desc",
        per_page, // Add rowsPerPage in the API request
        page
      };

      this.isRequestOngoing = true;
      let response;

      if (this.userType == "super_admin") {
        response = await this.fetchPayments(superadminData);
      } else if (this.userType == "funder") {
        response = await this.fetchPayments(funderData);
      } else if (this.userType == "anchor") {
        response = await this.fetchPayments(anchorData);
      } else {
        response = await this.fetchPayments(data);
      }

      this.isRequestOngoing = false;

      return {
        data: response.data.payments.data,
        pagination: {
          totalPages: response.data.payments.last_page,
          currentPage: page,
          count: response.data.payments.scount,
          rowsPerPage: response.data.payments.per_page // Add rowsPerPage in the API request
        }
      };
    },
    refreshTable() {
      this.$refs.table.refresh();
    },
    setFilters() {
      this.filtersApplied = true;
      this.refreshTable();
    },
    clearFilter() {
      if (this.filters.customer) {
        this.$refs.customerSelect.$refs.baseSelect.removeElement(
          this.filters.customer
        );
      }

      this.filters.user = "";
      this.filters.customer = "";
      this.filters.payment_mode = "";
      this.filters.payment_number = "";
      this.filters.receive = "";
      (this.filters.sent = ""),
        (this.filters.from_date = ""),
        (this.filters.to_date = ""),
        this.$nextTick(() => {
          this.filtersApplied = false;
        });
    },
    getAll() {
      this.filters.payment_mode = "";
      this.filters.payment_number = "";
      this.filters.receive = "";
      this.filters.sent = "";
    },
    getPaymentSent(val) {
      this.filters.payment_mode = "";
      this.filters.payment_number = "";
      this.filters.receive = "";
      this.filters.sent = val;
    },
    getPaymentReceive(val) {
      this.filters.payment_mode = "";
      this.filters.payment_number = "";
      this.filters.receive = val;
      this.filters.sent = "";
    },

    toggleFilter() {
      if (this.showFilters && this.filtersApplied) {
        this.clearFilter();
        this.refreshTable();
      }

      this.showFilters = !this.showFilters;
    },
    onSelectCustomer(customer) {
      this.filters.customer = customer;
    },
    async removePayment(id) {
      this.id = id;
      swal({
        title: this.$t("general.are_you_sure"),
        text: this.$tc("payments.confirm_delete"),
        icon: "/assets/icon/trash-solid.svg",
        buttons: true,
        dangerMode: true
      }).then(async willDelete => {
        if (willDelete) {
          let res = await this.deletePayment(this.id);
          if (res.data.success) {
            window.toastr["success"](this.$tc("payments.deleted_message", 1));
            this.$refs.table.refresh();
            return true;
          } else if (res.data.error) {
            window.toastr["error"](res.data.message);
          }
        }
      });
    },
    async removeMultiplePayments() {
      swal({
        title: this.$t("general.are_you_sure"),
        text: this.$tc("payments.confirm_delete", 2),
        icon: "/assets/icon/trash-solid.svg",
        buttons: true,
        dangerMode: true
      }).then(async willDelete => {
        if (willDelete) {
          let request = await this.deleteMultiplePayments();
          if (request.data.success) {
            window.toastr["success"](this.$tc("payments.deleted_message", 2));
            this.$refs.table.refresh();
          } else if (request.data.error) {
            window.toastr["error"](request.data.message);
          }
        }
      });
    },
    async clearCustomerSearch(removedOption, id) {
      this.filters.customer = "";
      this.$refs.table.refresh();
    },
    showModel(selectedRow) {
      this.selectedRow = selectedRow;
      this.$refs.Delete_modal.open();
    },
    async removeSelectedItems() {
      this.$refs.Delete_modal.close();
      await this.selectedRow.forEach(row => {
        this.deletePayment(this.id);
      });
      this.$refs.table.refresh();
    },
    addPayment() {
      if (this.userType == "anchor") {
        this.$router.push("/anchor/payments/create");
      } else if (this.userType == "supplier") {
        this.$router.push("/supplier/payments/create");
      } else if (this.userType == "funder") {
        this.$router.push("/funder/payments/create");
      }
    }
  }
};
</script>
